import React, { useState, useEffect } from 'react'
import { connect } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { get } from 'lodash'

import generateUuid from 'uuid/v4'

import { attemptPinLogin, clearAttemptPinLoginStatus, registerLogin } from '../actions/attendant'
import { cleanupState } from '../actions/shared'

import {
  getAttemptPinLoginStatus,
  getFetchAttendantsStatus,
  getEmployeeApprovalCodes,
  getAttendants,
  getEmployeeIds,
} from '../selectors/attendant'
import { getAttendantSettingsError } from '../selectors/attendantSettings'

import { ToastManager } from '../utils/toastManager'

import UserLoginComponent from '../components/userLogin/Component'
import NavMenu from '../components/NavMenu'
import { getCurrentStand, getCurrentMenu } from '../selectors/menus'

import createDataDogLog from '../utils/datadog'
import { clearCart } from '../actions/cart'
import { getIsSyncing, getIsAutomatic } from '../VNSyncManager/Selectors'
import { startBaseLoad, startDataLoad } from '../VNDataLoadManager/ActionCreators'

import useDeviceIdentifiers from '../VNUtil/hooks/useDeviceIdentifiers'
import { setShouldUpdate } from '../VNUpdateManager/ActionCreators'

import useSpinner from '../context/hooks/useSpinner'
import { LOADING_DATA_TEXT } from '../VNDataLoadManager/containers/VNDataLoadManager'

const UserLogin = ({
  approvalCodes,
  attemptPinLogin,
  attemptRegisterLogin,
  fetchAttendantsStatus,
  onMount,
  onSuccess,
  onUnmount,
  pinLoginStatus,
  pinLoginStatusText,
  venueName,
  employeeIds,
  attendants,
  isSyncing,
  isAutomaticSync,
}) => {
  const history = useHistory()

  const [showMenu, setShowMenu] = useState(false)
  const [pin, setPin] = useState('')
  const [isMounted, setIsMounted] = useState(false)

  const { getConditionalDeviceId } = useDeviceIdentifiers()

  const { startSpinner } = useSpinner()

  const onMenuToggle = () => {
    setShowMenu(!showMenu)
  }

  const getAttendant = (employeeId) => {
    const attendant = attendants?.find((employee) => employee.employeeId === employeeId)

    return attendant
  }

  const handleSubmit = (pin, isScan = false) => {
    if (fetchAttendantsStatus === 'loading') {
      ToastManager.error('Attendants are still being fetched. Please try again.')
    } else {
      if (isScan) {
        // pin can be the employee id if the user scanned a badge.
        const attendant = getAttendant(pin)
        setPin(attendant?.pinNumber)
        attemptPinLogin(attendant?.pinNumber)
        return
      }
      attemptPinLogin(pin)
      setPin(pin)
    }
  }

  useEffect(() => {
    if (!getConditionalDeviceId()) {
      history.replace('/register-device')
      return
    }

    onMount()
    setIsMounted(true)

    // Set the session ID for the user.
    // TODO (mkramerl): Make this session match "resets" rather than PIN-ins.
    try {
      localStorage.setItem('sessionId', generateUuid())
    } catch (e) {
      // Protect the app from crashing if we can't set the session ID.
      createDataDogLog('error', 'Failed to set session ID', e)
    }

    return () => {
      onUnmount()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (pinLoginStatus === 'succeeded') {
      startSpinner(LOADING_DATA_TEXT)
      onSuccess()
      attemptRegisterLogin(pin)
    }

    if (pinLoginStatus === 'failed') {
      ToastManager.error(pinLoginStatusText)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pinLoginStatus])

  return (
    <>
      <UserLoginComponent
        standName={venueName}
        pinLoginStatus={pinLoginStatus}
        pinLoginStatusText={pinLoginStatusText}
        handleSubmit={handleSubmit}
        onSuccess={onSuccess}
        approvalCodes={approvalCodes}
        fetchAttendantsStatus={fetchAttendantsStatus}
        isMounted={isMounted}
        employeeIds={employeeIds}
      />
      {showMenu && <NavMenu onToggle={onMenuToggle} />}
    </>
  )
}

const mapStateToProps = (state, ownProps) => {
  const pinLoginStatus = getAttemptPinLoginStatus(state)
  const errorMessage = getAttendantSettingsError(state)
  const menu = getCurrentMenu(state)
  const employeeIds = getEmployeeIds(state)
  const attendants = getAttendants(state)
  const approvalCodes = getEmployeeApprovalCodes(state)
  const fetchAttendantsStatus = getFetchAttendantsStatus(state)

  const PIN_LOGIN_STATUS_TEXT_MAP = {
    loading: 'Requesting Access',
    succeeded: 'Access Granted',
    failed: errorMessage,
  }

  const pinLoginStatusText = PIN_LOGIN_STATUS_TEXT_MAP[pinLoginStatus]

  const currentStand = getCurrentStand(state)

  const isSyncing = getIsSyncing(state)
  const isAutomaticSync = getIsAutomatic(state)

  return {
    venueName: menu.standName,
    pinLoginStatusText,
    pinLoginStatus,
    approvalCodes,
    fetchAttendantsStatus,
    currentStand,
    employeeIds,
    attendants,
    isSyncing,
    isAutomaticSync,
  }
}

const mapDispatchToProps = (dispatch, ownProps) => {
  const replace = get(ownProps, 'history.replace', () => {})

  return {
    onMount: () => {
      dispatch(setShouldUpdate(true))
      const accessToken = localStorage.getItem('accessToken')
      if (!accessToken) {
        replace('/device-login')
      } else {
        dispatch(startBaseLoad())
      }

      dispatch(clearAttemptPinLoginStatus())
      dispatch(cleanupState())
      dispatch(clearCart())
    },
    onUnmount: () => {
      dispatch(setShouldUpdate(false))
      dispatch(clearAttemptPinLoginStatus())
    },
    attemptPinLogin: (pin) => {
      dispatch(attemptPinLogin(pin))
    },
    attemptRegisterLogin: (pin) => {
      dispatch(registerLogin(pin))
    },
    onSuccess: () => {
      replace('/concession-order?from=user-login')

      dispatch(startDataLoad())
    },
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(UserLogin)
